import { DirectionsRenderer, GoogleMap } from "@react-google-maps/api";
import { useMemo, useEffect, useState, useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../../app/hooks";
import Markers from "./markers";
import { checkPointsDTO } from "../../../shared/dtos/checkPointsDTO";
import { RootState } from "../../../app/store";
import { DARK_STYLES } from "../../../shared/constants/DarkStyles";
import { snapToRoads } from "../../../services/googlemapApi";

type LatLngLiteral = google.maps.LatLngLiteral;
type MapOptions = google.maps.MapOptions;
type PathViewMode = "clean" | "raw";

interface TripLocationPoint {
  latitude: number;
  longitude: number;
  heading?: number;
  timestamp?: string;
}

interface MarkerPoint {
  latitude: number;
  longitude: number;
  name: string;
}

const getEnvNumber = (value: string | undefined, fallbackValue: number) => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? parsedValue
    : fallbackValue;
};

const START_RADIUS_METERS = getEnvNumber(
  process.env.REACT_APP_GROUP_TRIP_START_RADIUS_METERS,
  250,
);
const END_RADIUS_METERS = getEnvNumber(
  process.env.REACT_APP_GROUP_TRIP_END_RADIUS_METERS,
  250,
);
const END_TRAILING_BUFFER_POINTS = getEnvNumber(
  process.env.REACT_APP_GROUP_TRIP_END_TRAILING_BUFFER_POINTS,
  11,
);

interface InitialState {
  type?: string;
  trip?: TripLocationPoint[];
  originAddress?: string;
  originLat?: number;
  originLng?: number;
  destinationAddress?: string;
  destinationLat?: number;
  destinationLng?: number;
  waypoints?: checkPointsDTO[];
  margin?: string;
  height?: string;
  pathViewMode?: PathViewMode;
}

const toRadians = (value: number) => (value * Math.PI) / 180;

const getDistanceMeters = (
  startLatitude: number,
  startLongitude: number,
  endLatitude: number,
  endLongitude: number,
) => {
  const earthRadius = 6371000;
  const latitudeDelta = toRadians(endLatitude - startLatitude);
  const longitudeDelta = toRadians(endLongitude - startLongitude);
  const startLatitudeRadians = toRadians(startLatitude);
  const endLatitudeRadians = toRadians(endLatitude);
  const a =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(startLatitudeRadians) *
      Math.cos(endLatitudeRadians) *
      Math.sin(longitudeDelta / 2) ** 2;

  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const isValidCoordinate = (latitude?: number, longitude?: number) =>
  Number.isFinite(latitude) && Number.isFinite(longitude);

const getWaypointDestination = (
  waypoints?: checkPointsDTO[],
): { latitude?: number; longitude?: number; address?: string } => {
  if (!waypoints?.length) return {};

  const sortedWaypoints = [...waypoints].sort(
    (left, right) =>
      Number(left.requestSequence ?? left.id ?? 0) -
      Number(right.requestSequence ?? right.id ?? 0),
  );
  const lastWaypoint = sortedWaypoints[sortedWaypoints.length - 1];

  return {
    latitude: lastWaypoint?.latitude,
    longitude: lastWaypoint?.longitude,
    address: lastWaypoint?.address,
  };
};

const getFirstIndexWithinRadius = (
  points: TripLocationPoint[],
  latitude: number,
  longitude: number,
  radiusMeters: number,
) =>
  points.findIndex(
    (point) =>
      getDistanceMeters(
        point.latitude,
        point.longitude,
        latitude,
        longitude,
      ) <= radiusMeters,
  );

const getClosestIndex = (
  points: TripLocationPoint[],
  latitude: number,
  longitude: number,
  startIndex = 0,
) => {
  let closestIndex = startIndex;
  let closestDistance = Number.POSITIVE_INFINITY;

  for (let index = startIndex; index < points.length; index++) {
    const point = points[index];
    const distance = getDistanceMeters(
      point.latitude,
      point.longitude,
      latitude,
      longitude,
    );
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  }

  return closestIndex;
};

const normalizeTripPoints = (trip?: TripLocationPoint[]) =>
  (trip ?? []).filter((point) =>
    isValidCoordinate(Number(point?.latitude), Number(point?.longitude)),
  );

const buildCleanTripPoints = ({
  trip,
  originLat,
  originLng,
  destinationLat,
  destinationLng,
}: {
  trip: TripLocationPoint[];
  originLat?: number;
  originLng?: number;
  destinationLat?: number;
  destinationLng?: number;
}) => {
  if (
    trip.length < 2 ||
    !isValidCoordinate(originLat, originLng) ||
    !isValidCoordinate(destinationLat, destinationLng)
  ) {
    return trip;
  }

  const safeOriginLat = Number(originLat);
  const safeOriginLng = Number(originLng);
  const safeDestinationLat = Number(destinationLat);
  const safeDestinationLng = Number(destinationLng);

  const firstOriginIndex = getFirstIndexWithinRadius(
    trip,
    safeOriginLat,
    safeOriginLng,
    START_RADIUS_METERS,
  );
  const startIndex =
    firstOriginIndex >= 0
      ? firstOriginIndex
      : getClosestIndex(trip, safeOriginLat, safeOriginLng);

  const slicedTrip = trip.slice(startIndex);
  if (!slicedTrip.length) return trip;

  const firstDestinationIndex = getFirstIndexWithinRadius(
    slicedTrip,
    safeDestinationLat,
    safeDestinationLng,
    END_RADIUS_METERS,
  );
  const relativeEndIndex =
    firstDestinationIndex >= 0
      ? firstDestinationIndex
      : getClosestIndex(slicedTrip, safeDestinationLat, safeDestinationLng);
  const endIndex = startIndex + relativeEndIndex;
  const bufferedEndIndex = Math.min(
    trip.length - 1,
    endIndex + END_TRAILING_BUFFER_POINTS,
  );

  if (endIndex <= startIndex) {
    return trip;
  }

  const cleanTrip = trip.slice(startIndex, bufferedEndIndex + 1);

  if (!cleanTrip.length) {
    return trip;
  }

  const lastPoint = cleanTrip[cleanTrip.length - 1];
  const destinationDistance = getDistanceMeters(
    lastPoint.latitude,
    lastPoint.longitude,
    safeDestinationLat,
    safeDestinationLng,
  );

  if (destinationDistance > END_RADIUS_METERS) {
    return trip.slice(startIndex, bufferedEndIndex + 1);
  }

  return cleanTrip;
};

const GroupTripsMap = ({
  type = "trip",
  trip,
  originAddress,
  originLat,
  originLng,
  destinationAddress,
  destinationLat,
  destinationLng,
  waypoints,
  margin,
  height,
  pathViewMode = "clean",
}: InitialState): JSX.Element => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  let passegers = useAppSelector((state) => state.passeger.value);
  const dispatch = useAppDispatch();
  const darkMode: boolean = useAppSelector(
    (state: RootState) => state.currentAdmin.value.darkMode,
  );

  const directionsResponse = useAppSelector(
    (state) => state.groupTripsMap.directionsResponse,
  );

  const center = useMemo<LatLngLiteral>(
    () => ({ lat: -34.88582, lng: -56.17122 }),
    [],
  );

  const options = useMemo<MapOptions>(
    () => ({
      streetViewControl: false,
    }),
    [],
  );

  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const [refinedTrip, setRefinedTrip] = useState<TripLocationPoint[] | null>(null);
  const normalizedTrip = useMemo(() => normalizeTripPoints(trip), [trip]);

  const finalWaypoint = useMemo(() => getWaypointDestination(waypoints), [waypoints]);

  const plannedDestination = useMemo(
    () => ({
      latitude: destinationLat ?? finalWaypoint.latitude,
      longitude: destinationLng ?? finalWaypoint.longitude,
      name: destinationAddress || finalWaypoint.address || "Destino",
    }),
    [destinationAddress, destinationLat, destinationLng, finalWaypoint],
  );

  const cleanTrip = useMemo(
    () =>
      buildCleanTripPoints({
        trip: normalizedTrip,
        originLat,
        originLng,
        destinationLat: plannedDestination.latitude,
        destinationLng: plannedDestination.longitude,
      }),
    [
      normalizedTrip,
      originLat,
      originLng,
      plannedDestination.latitude,
      plannedDestination.longitude,
    ],
  );

  const displayTrip = useMemo(
    () => (pathViewMode === "raw" ? normalizedTrip : cleanTrip),
    [cleanTrip, normalizedTrip, pathViewMode],
  );

  const activeTripData = useMemo(
    () => (pathViewMode === "clean" ? refinedTrip || displayTrip : displayTrip),
    [displayTrip, pathViewMode, refinedTrip],
  );

  const startMarker = useMemo<MarkerPoint | null>(() => {
    if (pathViewMode === "clean" && isValidCoordinate(originLat, originLng)) {
      return {
        latitude: Number(originLat),
        longitude: Number(originLng),
        name: originAddress || "Origen",
      };
    }

    const firstPoint = activeTripData[0];
    if (!firstPoint) return null;

    return {
      latitude: Number(firstPoint.latitude),
      longitude: Number(firstPoint.longitude),
      name: originAddress || "Inicio real",
    };
  }, [activeTripData, originAddress, originLat, originLng, pathViewMode]);

  const endMarker = useMemo<MarkerPoint | null>(() => {
    if (
      pathViewMode === "clean" &&
      isValidCoordinate(plannedDestination.latitude, plannedDestination.longitude)
    ) {
      return {
        latitude: Number(plannedDestination.latitude),
        longitude: Number(plannedDestination.longitude),
        name: plannedDestination.name,
      };
    }

    const lastPoint = activeTripData[activeTripData.length - 1];
    if (!lastPoint) return null;

    return {
      latitude: Number(lastPoint.latitude),
      longitude: Number(lastPoint.longitude),
      name: plannedDestination.name || "Fin real",
    };
  }, [activeTripData, pathViewMode, plannedDestination]);

  useEffect(() => {
    let isMounted = true;

    if (type !== "tripDetails" || pathViewMode !== "clean" || displayTrip.length < 2) {
      setRefinedTrip(null);
      return () => {
        isMounted = false;
      };
    }

    void snapToRoads(displayTrip).then((snappedTrip) => {
      if (!isMounted) return;
      setRefinedTrip(snappedTrip);
    });
    
    return () => {
      isMounted = false;
    };

  }, [displayTrip, pathViewMode, type]);

  useEffect(() => {
    const markMap = () => {
      if (!map) return;

      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }

      const tripCoordinates: google.maps.LatLng[] = [];
      const bounds = new google.maps.LatLngBounds();

      const tripLength = activeTripData?.length || 0;
      for (let i = 0; i < tripLength; i++) {
        if (activeTripData[i]?.latitude != null && activeTripData[i]?.longitude != null) {
          const point = new google.maps.LatLng(
            Number(activeTripData[i].latitude),
            Number(activeTripData[i].longitude),
          );
          bounds.extend(point);
          tripCoordinates.push(point);
        }
      }

      if (startMarker) {
        bounds.extend(
          new google.maps.LatLng(startMarker.latitude, startMarker.longitude),
        );
      }

      if (endMarker) {
        bounds.extend(
          new google.maps.LatLng(endMarker.latitude, endMarker.longitude),
        );
      }

      if (tripCoordinates.length > 1) {
        const tripPath = new google.maps.Polyline({
          path: tripCoordinates,
          geodesic: true,
          strokeColor: "blue",
          strokeOpacity: 0.8,
          strokeWeight: 3,
          icons: [{
            icon: {
              path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
              scale: 4,
              strokeColor: "white",
              strokeWeight: 1,
              fillColor: "blue",
              fillOpacity: 1,
            },
            offset: '0',
            repeat: '50px'
          }],
        });

        tripPath.setMap(map);
        polylineRef.current = tripPath;
      }

      if (!bounds.isEmpty()) {
        map.fitBounds(bounds);
      } else if (tripCoordinates.length === 1) {
        map.panTo(tripCoordinates[0]);
        map.setZoom(15);
      }
    };

    if (type === "tripDetails") {
      markMap();
    }

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, activeTripData, endMarker, startMarker, type]);

  useEffect(() => {
    if (type !== "tripDetails" && directionsResponse === null) {
      map?.panTo(center);
      map?.setZoom(12);
    }
  }, [directionsResponse, center, map, dispatch, type]);

  return (
    <>
      <GoogleMap
        center={center}
        zoom={12}
        mapContainerStyle={{
          margin: margin ?? "0 10px 0 10px",
          height: height ?? "80vh",
          width: "100%",
        }}
        options={
          darkMode ? { ...options, ...DARK_STYLES } : { ...options, styles: [] }
        }
        onLoad={(map) => setMap(map)}
      >
        {directionsResponse && type !== "tripDetails" && (
          <DirectionsRenderer directions={directionsResponse} />
        )}
        {type === "passeger" &&
          passegers.map((p) => {
            return (
              <div key={p.id}>
                <Markers
                  id={`${p.id}-origin`}
                  key={`${p.id}-origin`}
                  lat={p.origin.coordinate.lat}
                  lng={p.origin.coordinate.lng}
                  name={p.name}
                  origin={true}
                />
                <Markers
                  id={`${p.id}-destiny`}
                  key={`${p.id}-destiny`}
                  lat={p.destiny.coordinate.lat}
                  lng={p.destiny.coordinate.lng}
                  name={p.destiny.name}
                  origin={false}
                />
              </div>
            );
          })}
        {type === "tripDetails" && activeTripData.length > 0 && (
          <div key="trip-details">
            {startMarker && (
              <Markers
                id="origin"
                key="origin"
                lat={startMarker.latitude}
                lng={startMarker.longitude}
                name={startMarker.name}
                origin={true}
                label="A"
                color="red"
              />
            )}
            {endMarker && activeTripData.length > 1 && (
              <Markers
                id="destination"
                key="destination"
                lat={endMarker.latitude}
                lng={endMarker.longitude}
                name={endMarker.name}
                origin={false}
                label="B"
                color="red"
              />
            )}
          </div>
        )}
      </GoogleMap>
    </>
  );
};

export default GroupTripsMap;
